import { useEffect, useState } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import apiClient from "@/lib/api-client";
import { setAppIconBadge } from "@/lib/web-push";

/**
 * Piezas compartidas de los accesos a Conversaciones (14-sep-2026): el icono
 * del navbar, la bola flotante de WhatsApp y la bola de ayuda (que se mueve
 * a la izquierda cuando la de WhatsApp está presente) beben de aquí para no
 * divergir jamás en criterio.
 */

interface AddOnListItem {
  code: string;
  subscription?: { status: string } | null;
}

// Caché de módulo: el resultado no cambia durante la sesión y evita que las
// bolas "salten" de sitio en cada navegación mientras se re-consulta.
let cachedHasWhatsAppAi: boolean | null = null;

/**
 * ¿La organización tiene WhatsApp IA activo (suelto o en el pack)? `null`
 * mientras se comprueba. Mismo criterio y mismo fail-open que el candado de
 * venta de Conversations.tsx: un fallo de red jamás esconde la función a un
 * cliente que paga (como mucho se la enseña a uno que no — y le lleva a la
 * pantalla de contratación, que es honesta).
 */
export function useWhatsAppAiAddon(): boolean | null {
  const [hasAddon, setHasAddon] = useState<boolean | null>(cachedHasWhatsAppAi);

  useEffect(() => {
    if (cachedHasWhatsAppAi !== null) return;
    let active = true;
    apiClient
      .get<{ success: true; data: AddOnListItem[] }>("/add-ons")
      .then((res) => {
        if (!active) return;
        const rows = res.data.data ?? [];
        // "Redes sociales IA" (Instagram y Messenger) usa el mismo inbox.
        const hasIt = ["whatsapp_ai", "recepcionista_ia", "social_ai"].some(
          (code) => rows.find((a) => a.code === code)?.subscription?.status === "active",
        );
        cachedHasWhatsAppAi = hasIt;
        setHasAddon(hasIt);
      })
      .catch(() => {
        if (active) {
          cachedHasWhatsAppAi = true; // fail-open (ver arriba)
          setHasAddon(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  return hasAddon;
}

/**
 * Conversaciones que ESPERAN A UNA PERSONA (modo humano con no-leídos — las
 * que lleva Carmen no suman: no piden acción). Refresco cada 60s y al
 * navegar. El endpoint nunca da 500; el badge nunca rompe nada.
 */
export interface ConversationCounts {
  /** Esperan a una persona (modo humano con no-leídos). */
  humanCount: number;
  /** Con algo sin leer, las lleve quien las lleve (contador de la bola). */
  unread: number;
}

/**
 * `basePath` (18-sep-2026): la bola del superadmin cuenta contra
 * `/admin/platform-conversations` en vez de `/whatsapp-conversations` (su
 * inbox de soporte, no el del negocio seleccionado). OJO — diferencia real
 * de contrato: `/admin/platform-conversations/count` solo devuelve
 * `humanCount` (sin `unreadConversations`, ver `getPlatformConversationsCount`
 * en el backend); por eso el contador de la bola cae a `humanCount` cuando
 * `unreadConversations` no viene, en vez de mostrar siempre 0.
 */
export function useConversationsPendingCount(
  enabled: boolean,
  basePath: string = "/whatsapp-conversations",
): ConversationCounts {
  const routerLocation = useRouterLocation();
  const [counts, setCounts] = useState<ConversationCounts>({ humanCount: 0, unread: 0 });

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const load = () => {
      apiClient
        .get<{
          success: true;
          data: { humanCount: number; unreadConversations?: number };
        }>(`${basePath}/count`)
        .then((res) => {
          if (active) {
            const unread = res.data.data.unreadConversations ?? res.data.data.humanCount ?? 0;
            setCounts({
              humanCount: res.data.data.humanCount ?? 0,
              unread,
            });
            // Numerito del icono del PWA instalado: se corrige solo en cada
            // sondeo (60s) y se limpia al quedar a cero.
            setAppIconBadge(unread);
          }
        })
        .catch(() => {
          // Silencioso a propósito.
        });
    };
    load();
    const timer = setInterval(load, 60_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [enabled, routerLocation.pathname, basePath]);

  return counts;
}
